import { MutableRefObject, RefObject } from "react";
import { PixelCrop } from "react-image-crop";

interface cropOptions {
	x: number;
	y: number;
	width: number;
	height: number;
	[key: string]: any;
}

export const sliceImage = async (
	img: string | Blob,
	slices: cropOptions[],
	callback?: (b64: string, opts: cropOptions) => any,
): Promise<any[]> => {
	return await new Promise((resolve, reject) => {
		const image = new Image();

		image.addEventListener("load", () => {
			const canvas = document.createElement("canvas");
			const context = canvas.getContext("2d");

			const slicedImg = slices.map((sliceOpts) => {
				const { x, y, width, height } = sliceOpts;
				canvas.width = width !== undefined ? width : image.naturalWidth;
				canvas.height = height !== undefined ? height : image.naturalHeight;

				context?.drawImage(
					image,
					x || 0,
					y || 0,
					image.naturalWidth,
					image.naturalHeight,
					0,
					0,
					image.naturalWidth,
					image.naturalHeight,
				);

				const b64 = canvas.toDataURL();

				if (callback) {
					return callback(b64, sliceOpts);
				}

				return b64;
			});

			resolve(slicedImg);
		});

		image.addEventListener("error", () => {
			reject(new Error());
		});

		if (typeof img === "string") {
			image.src = img;
		} else {
			image.src = URL.createObjectURL(img);
		}
	});
};

const TO_RADIANS = Math.PI / 180;

export const canvasPreview = async (
	image: HTMLImageElement,
	canvas: HTMLCanvasElement,
	crop: PixelCrop,
	scale = 1,
	rotate = 0,
) => {
	const ctx = canvas.getContext("2d");

	if (!ctx) {
		return;
	}

	const scaleX = image.naturalWidth / image.width;
	const scaleY = image.naturalHeight / image.height;
	const pixelRatio = window.devicePixelRatio;

	canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
	canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

	ctx.scale(pixelRatio, pixelRatio);
	ctx.imageSmoothingQuality = "high";

	const cropX = crop.x * scaleX;
	const cropY = crop.y * scaleY;

	const rotateRads = rotate * TO_RADIANS;
	const centerX = image.naturalWidth / 2;
	const centerY = image.naturalHeight / 2;

	ctx.save();

	ctx.translate(-cropX, -cropY);
	ctx.translate(centerX, centerY);
	ctx.rotate(rotateRads);
	ctx.scale(scale, scale);
	ctx.translate(-centerX, -centerY);
	ctx.drawImage(
		image,
		0,
		0,
		image.naturalWidth,
		image.naturalHeight,
		0,
		0,
		image.naturalWidth,
		image.naturalHeight,
	);

	ctx.restore();

	return await canvas.toDataURL();
};

export const convertCanvasToBlob = async (
	img: HTMLImageElement,
	previewCanvas: HTMLCanvasElement,
	crop: PixelCrop,
) => {
	const offscreen = new OffscreenCanvas(crop.width, crop.height);
	const ctx = offscreen.getContext("2d");

	if (!ctx) {
		throw new Error("No 2d context");
	}

	ctx.drawImage(
		previewCanvas,
		0,
		0,
		previewCanvas.width,
		previewCanvas.height,
		0,
		0,
		offscreen.width,
		offscreen.height,
	);

	const blob = await offscreen.convertToBlob({
		type: "image/png",
	});

	return blob;
};
